/**
 * Column sorting for every table (ClickUp z8nrz7e4h6).
 *
 * Pure — no React — so vitest covers it. The header cycles a column
 * ascending → descending → unsorted (the table's own order); the comparison
 * follows the value's TYPE, not its text: dates chronologically, numbers
 * numerically ("10" after "9"), text case-insensitively, booleans false first.
 * Empty values always sort last, in both directions, so a descending sort never
 * opens on a screen of blanks. Ties keep the incoming order (stable).
 */

export type SortDir = 'asc' | 'desc'

export interface SortState {
  id: string
  dir: SortDir
}

/** What a column contributes to a comparison. */
export type SortValue = string | number | boolean | Date | null | undefined

/** One accessor per sortable column id: the row's value for that column. */
export type SortAccessors<T> = Record<string, (row: T) => SortValue>

/** The next state when a column's header is clicked. */
export function nextSort(current: SortState | null | undefined, id: string): SortState | null {
  if (!current || current.id !== id) return { id, dir: 'asc' }
  return current.dir === 'asc' ? { id, dir: 'desc' } : null
}

/** A leading ISO date — "2026-09-28" or a full timestamp — compares as a date. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}(?:[T ][\d:.]+(?:Z|[+-]\d{2}:?\d{2})?)?$/

function isEmpty(v: SortValue): boolean {
  return v === null || v === undefined || (typeof v === 'string' && v.trim() === '') || (typeof v === 'number' && Number.isNaN(v))
}

function asComparable(v: Exclude<SortValue, null | undefined>): number | string {
  if (v instanceof Date) return v.getTime()
  if (typeof v === 'boolean') return v ? 1 : 0
  if (typeof v === 'number') return v
  const s = v.trim()
  if (ISO_DATE.test(s)) {
    const t = Date.parse(s)
    if (!Number.isNaN(t)) return t
  }
  return s
}

const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })

/** Ascending comparison of two NON-empty values. */
export function compareSortValues(a: Exclude<SortValue, null | undefined>, b: Exclude<SortValue, null | undefined>): number {
  const x = asComparable(a)
  const y = asComparable(b)
  if (typeof x === 'number' && typeof y === 'number') return x - y
  return collator.compare(String(x), String(y))
}

/**
 * The rows in sorted order — a new array; the input is not touched. Unknown or
 * absent sort (or a column with no accessor) returns the rows as given.
 */
export function sortRows<T>(rows: readonly T[], sort: SortState | null | undefined, accessors: SortAccessors<T>): T[] {
  const accessor = sort ? accessors[sort.id] : undefined
  if (!sort || !accessor) return [...rows]
  const sign = sort.dir === 'asc' ? 1 : -1
  return rows
    .map((row, index) => ({ row, index, value: accessor(row) }))
    .sort((a, b) => {
      const ae = isEmpty(a.value)
      const be = isEmpty(b.value)
      if (ae || be) return ae === be ? a.index - b.index : ae ? 1 : -1
      const c = compareSortValues(a.value as never, b.value as never)
      return c !== 0 ? sign * c : a.index - b.index
    })
    .map((x) => x.row)
}

/** `name:asc` — the query value a server-sorted list sends. */
export function sortParam(sort: SortState | null | undefined): string | undefined {
  return sort ? `${sort.id}:${sort.dir}` : undefined
}
